/*
 * Quartz Design System — Semantic Color Tokens
 * Maps primitive colors to their intended usage roles.
 * Source: Quartz v3.0 2026 — Figma Color Guidelines
 * Note: "tag" covers both badge and pill components
 * Updated: 2026-06-17
 */

import { PRIMITIVE_COLORS, deepFreeze } from './primitive_colors';

export const COLOR_TOKENS = deepFreeze({
    // ─────────────────────────────────────────
    // BACKGROUND
    // ─────────────────────────────────────────
    background: {
        primary: PRIMITIVE_COLORS.neutral.white,
        secondary: PRIMITIVE_COLORS.neutral.graphite[50],
        tertiaryLight: PRIMITIVE_COLORS.secondary.tan[100],
        tertiaryDefault: PRIMITIVE_COLORS.secondary.tan[500],
        inverse: PRIMITIVE_COLORS.neutral.black,
        overlay: PRIMITIVE_COLORS.overlay.default.rgba,
        hover: PRIMITIVE_COLORS.primary.aqua[50],
        active: PRIMITIVE_COLORS.primary.aqua[100],
        brand: PRIMITIVE_COLORS.brand.orange[50],
        error: PRIMITIVE_COLORS.semantic.red[50],
        warning: PRIMITIVE_COLORS.semantic.yellow[50],
        success: PRIMITIVE_COLORS.semantic.green[50],
        info: PRIMITIVE_COLORS.semantic.blue[50],
        accentPurple: PRIMITIVE_COLORS.accent.purple[50],
        accentCobalt: PRIMITIVE_COLORS.accent.cobalt[50],
    },

    // ─────────────────────────────────────────
    // TEXT
    // ─────────────────────────────────────────
    text: {
        primary: PRIMITIVE_COLORS.neutral.black,
        secondary: PRIMITIVE_COLORS.neutral.graphite[600],
        disabled: PRIMITIVE_COLORS.neutral.graphite[300],
        inverse: PRIMITIVE_COLORS.neutral.white,
        selected: PRIMITIVE_COLORS.accent.cobalt[500],
        link: PRIMITIVE_COLORS.accent.cobalt[600],
        linkHover: PRIMITIVE_COLORS.accent.cobalt[700],
        brand: PRIMITIVE_COLORS.brand.orange[500],
        error: PRIMITIVE_COLORS.semantic.red[500],
        warning: PRIMITIVE_COLORS.semantic.yellow[500],
        success: PRIMITIVE_COLORS.semantic.green[500],
        info: PRIMITIVE_COLORS.semantic.blue[500],
        accentPurple: PRIMITIVE_COLORS.accent.purple[500],
        accentCobalt: PRIMITIVE_COLORS.accent.cobalt[500],
    },

    // ─────────────────────────────────────────
    // BORDER
    // ─────────────────────────────────────────
    border: {
        default: PRIMITIVE_COLORS.neutral.graphite[300],
        subtle: PRIMITIVE_COLORS.neutral.graphite[200],
        strong: PRIMITIVE_COLORS.neutral.graphite[400],
        disabled: PRIMITIVE_COLORS.neutral.graphite[100],
        tertiary: PRIMITIVE_COLORS.secondary.tan[500],
        selected: PRIMITIVE_COLORS.accent.cobalt[500],
        brand: PRIMITIVE_COLORS.brand.orange[500],
        error: PRIMITIVE_COLORS.semantic.red[500],
        warning: PRIMITIVE_COLORS.semantic.yellow[500],
        success: PRIMITIVE_COLORS.semantic.green[500],
        info: PRIMITIVE_COLORS.semantic.blue[500],
        accentPurple: PRIMITIVE_COLORS.accent.purple[500],
        accentCobalt: PRIMITIVE_COLORS.accent.cobalt[500],
    },

    // ─────────────────────────────────────────
    // ICON
    // ─────────────────────────────────────────
    icon: {
        default: PRIMITIVE_COLORS.neutral.black,
        subtle: PRIMITIVE_COLORS.neutral.graphite[700],
        inverse: PRIMITIVE_COLORS.neutral.white,
        disabled: PRIMITIVE_COLORS.neutral.graphite[400],
        selected: PRIMITIVE_COLORS.accent.cobalt[500],
        brand: PRIMITIVE_COLORS.brand.orange[500],
        error: PRIMITIVE_COLORS.semantic.red[500],
        warning: PRIMITIVE_COLORS.semantic.yellow[500],
        success: PRIMITIVE_COLORS.semantic.green[500],
        info: PRIMITIVE_COLORS.semantic.blue[500],
    },

    // ─────────────────────────────────────────
    // BUTTON
    // ─────────────────────────────────────────
    button: {
        primary: {
            background: PRIMITIVE_COLORS.primary.aqua[500],
            backgroundHover: PRIMITIVE_COLORS.primary.aqua[600],
            backgroundActive: PRIMITIVE_COLORS.primary.aqua[700],
            backgroundDisabled: PRIMITIVE_COLORS.primary.aqua[100],
            text: PRIMITIVE_COLORS.neutral.black,
            textDisabled: PRIMITIVE_COLORS.neutral.graphite[300],
            icon: PRIMITIVE_COLORS.neutral.black,
            iconDisabled: PRIMITIVE_COLORS.neutral.graphite[300],
        },
        secondary: {
            background: PRIMITIVE_COLORS.neutral.white,
            backgroundHover: PRIMITIVE_COLORS.primary.aqua[50],
            backgroundActive: PRIMITIVE_COLORS.primary.aqua[100],
            backgroundDisabled: PRIMITIVE_COLORS.neutral.white,
            border: PRIMITIVE_COLORS.primary.aqua[500],
            borderDisabled: PRIMITIVE_COLORS.primary.aqua[300],
            text: PRIMITIVE_COLORS.primary.aqua[800],
            textDisabled: PRIMITIVE_COLORS.neutral.graphite[300],
            icon: PRIMITIVE_COLORS.primary.aqua[800],
            iconDisabled: PRIMITIVE_COLORS.neutral.graphite[300],
        },
        tertiary: {
            backgroundHover: PRIMITIVE_COLORS.primary.aqua[50],
            backgroundActive: PRIMITIVE_COLORS.primary.aqua[100],
            text: PRIMITIVE_COLORS.primary.aqua[800],
            textDisabled: PRIMITIVE_COLORS.neutral.graphite[300],
            icon: PRIMITIVE_COLORS.primary.aqua[800],
            iconDisabled: PRIMITIVE_COLORS.neutral.graphite[300],
        },
        destructive: {
            backgroundHover: PRIMITIVE_COLORS.semantic.red[50],
            backgroundActive: PRIMITIVE_COLORS.semantic.red[100],
            border: PRIMITIVE_COLORS.semantic.red[500],
            borderDisabled: PRIMITIVE_COLORS.semantic.red[200],
            text: PRIMITIVE_COLORS.semantic.red[500],
            textDisabled: PRIMITIVE_COLORS.semantic.red[200],
            icon: PRIMITIVE_COLORS.semantic.red[500],
            iconDisabled: PRIMITIVE_COLORS.semantic.red[200],
        },
    },

    // ─────────────────────────────────────────
    // BRAND BUTTON
    // NEW: added from Figma update 2026-06-17
    // ─────────────────────────────────────────
    brandButton: {
        primary: {
            background: PRIMITIVE_COLORS.brand.orange[500],
            backgroundHover: PRIMITIVE_COLORS.brand.orange[600],
            backgroundActive: PRIMITIVE_COLORS.brand.orange[700],
            backgroundDisabled: PRIMITIVE_COLORS.brand.orange[50],
            text: PRIMITIVE_COLORS.neutral.white,
            textDisabled: PRIMITIVE_COLORS.neutral.graphite[300],
            icon: PRIMITIVE_COLORS.neutral.white,
            iconDisabled: PRIMITIVE_COLORS.neutral.graphite[300],
        },
        secondary: {
            background: PRIMITIVE_COLORS.neutral.white,
            backgroundHover: PRIMITIVE_COLORS.brand.orange[50],
            backgroundActive: PRIMITIVE_COLORS.brand.orange[100],
            backgroundDisabled: PRIMITIVE_COLORS.brand.orange[50],
            border: PRIMITIVE_COLORS.brand.orange[500],
            borderDisabled: PRIMITIVE_COLORS.brand.orange[300],
            text: PRIMITIVE_COLORS.brand.orange[500],
            textDisabled: PRIMITIVE_COLORS.neutral.graphite[300],
            icon: PRIMITIVE_COLORS.brand.orange[500],
            iconDisabled: PRIMITIVE_COLORS.neutral.graphite[300],
        },
        tertiary: {
            backgroundHover: PRIMITIVE_COLORS.brand.orange[50],
            backgroundActive: PRIMITIVE_COLORS.brand.orange[100],
            text: PRIMITIVE_COLORS.brand.orange[500],
            textDisabled: PRIMITIVE_COLORS.neutral.graphite[300],
            icon: PRIMITIVE_COLORS.brand.orange[500],
            iconDisabled: PRIMITIVE_COLORS.neutral.graphite[300],
        },
    },

    // ─────────────────────────────────────────
    // FORM
    // ─────────────────────────────────────────
    form: {
        input: {
            background: PRIMITIVE_COLORS.neutral.white,
            backgroundDisabled: PRIMITIVE_COLORS.neutral.graphite[50],
            border: PRIMITIVE_COLORS.neutral.graphite[300],
            borderHover: PRIMITIVE_COLORS.accent.cobalt[500],
            borderFocus: PRIMITIVE_COLORS.accent.cobalt[500],
            borderActive: PRIMITIVE_COLORS.accent.cobalt[500],
            borderError: PRIMITIVE_COLORS.semantic.red[500],
            borderDisabled: PRIMITIVE_COLORS.neutral.graphite[100],
            text: PRIMITIVE_COLORS.neutral.graphite[700],
            textDisabled: PRIMITIVE_COLORS.neutral.graphite[400],
            textPlaceholder: PRIMITIVE_COLORS.neutral.graphite[400],
            label: PRIMITIVE_COLORS.neutral.graphite[700],
            labelError: PRIMITIVE_COLORS.semantic.red[500],
            helper: PRIMITIVE_COLORS.neutral.graphite[500],
            helperError: PRIMITIVE_COLORS.semantic.red[500],
            icon: PRIMITIVE_COLORS.neutral.graphite[700],
            iconDisabled: PRIMITIVE_COLORS.neutral.graphite[400],
        },
        checkboxRadio: {
            empty: PRIMITIVE_COLORS.neutral.black,
            selected: PRIMITIVE_COLORS.accent.cobalt[500],
            disabled: PRIMITIVE_COLORS.neutral.graphite[300],
            checkmark: PRIMITIVE_COLORS.neutral.white,
        },
        switch: {
            backgroundOff: PRIMITIVE_COLORS.neutral.grey[400],
            backgroundOn: PRIMITIVE_COLORS.semantic.green[400],
            backgroundDisabledOff: PRIMITIVE_COLORS.neutral.grey[100],
            backgroundDisabledOn: PRIMITIVE_COLORS.semantic.green[100],
            handle: PRIMITIVE_COLORS.neutral.graphite[100],
            handleDisabled: PRIMITIVE_COLORS.neutral.graphite[50],
        },
    },

    // ─────────────────────────────────────────
    // TABLE
    // ─────────────────────────────────────────
    table: {
        background: PRIMITIVE_COLORS.neutral.white,
        backgroundStripe: PRIMITIVE_COLORS.neutral.graphite[50],
        backgroundHover: PRIMITIVE_COLORS.primary.aqua[50],
        backgroundSelected: PRIMITIVE_COLORS.primary.aqua[100],
        backgroundHeader: PRIMITIVE_COLORS.semantic.blue[50],
        border: PRIMITIVE_COLORS.neutral.graphite[300],
        text: PRIMITIVE_COLORS.neutral.black,
        textSecondary: PRIMITIVE_COLORS.neutral.graphite[600],
        textHeader: PRIMITIVE_COLORS.neutral.black,
        icon: PRIMITIVE_COLORS.neutral.black,
    },

    // ─────────────────────────────────────────
    // FEEDBACK
    // ─────────────────────────────────────────
    feedback: {
        neutral: {
            background: PRIMITIVE_COLORS.neutral.graphite[50],
            border: PRIMITIVE_COLORS.neutral.graphite[500],
            text: PRIMITIVE_COLORS.neutral.graphite[800],
            icon: PRIMITIVE_COLORS.neutral.graphite[600],
        },
        error: {
            background: PRIMITIVE_COLORS.semantic.red[50],
            border: PRIMITIVE_COLORS.semantic.red[500],
            text: PRIMITIVE_COLORS.semantic.red[800],
            icon: PRIMITIVE_COLORS.semantic.red[600],
        },
        warning: {
            background: PRIMITIVE_COLORS.semantic.yellow[50],
            border: PRIMITIVE_COLORS.semantic.yellow[600],
            text: PRIMITIVE_COLORS.semantic.yellow[800],
            icon: PRIMITIVE_COLORS.semantic.yellow[700],
        },
        success: {
            background: PRIMITIVE_COLORS.semantic.green[50],
            border: PRIMITIVE_COLORS.semantic.green[500],
            text: PRIMITIVE_COLORS.semantic.green[800],
            icon: PRIMITIVE_COLORS.semantic.green[600],
        },
        info: {
            background: PRIMITIVE_COLORS.semantic.blue[50],
            border: PRIMITIVE_COLORS.semantic.blue[600],
            text: PRIMITIVE_COLORS.semantic.blue[800],
            icon: PRIMITIVE_COLORS.semantic.blue[700],
        },
    },

    // ─────────────────────────────────────────
    // TAG (covers badge and pill)
    // ─────────────────────────────────────────
    tag: {
        brand: {
            background: PRIMITIVE_COLORS.brand.orange[500],
            backgroundOutline: PRIMITIVE_COLORS.brand.orange[50],
            border: PRIMITIVE_COLORS.brand.orange[500],
            text: PRIMITIVE_COLORS.neutral.white,
            textOutline: PRIMITIVE_COLORS.brand.orange[600],
            icon: PRIMITIVE_COLORS.neutral.white,
            iconOutline: PRIMITIVE_COLORS.brand.orange[600],
        },
        neutral: {
            background: PRIMITIVE_COLORS.neutral.graphite[500],
            backgroundOutline: PRIMITIVE_COLORS.neutral.graphite[50],
            border: PRIMITIVE_COLORS.neutral.graphite[500],
            text: PRIMITIVE_COLORS.neutral.white,
            textOutline: PRIMITIVE_COLORS.neutral.graphite[600],
            icon: PRIMITIVE_COLORS.neutral.white,
            iconOutline: PRIMITIVE_COLORS.neutral.graphite[600],
        },
        error: {
            background: PRIMITIVE_COLORS.semantic.red[500],
            backgroundOutline: PRIMITIVE_COLORS.semantic.red[50],
            border: PRIMITIVE_COLORS.semantic.red[500],
            text: PRIMITIVE_COLORS.neutral.white,
            textOutline: PRIMITIVE_COLORS.semantic.red[600],
            icon: PRIMITIVE_COLORS.neutral.white,
            iconOutline: PRIMITIVE_COLORS.semantic.red[600],
        },
        warning: {
            background: PRIMITIVE_COLORS.semantic.yellow[600],
            backgroundOutline: PRIMITIVE_COLORS.semantic.yellow[50],
            border: PRIMITIVE_COLORS.semantic.yellow[600],
            text: PRIMITIVE_COLORS.neutral.white,
            textOutline: PRIMITIVE_COLORS.semantic.yellow[700],
            icon: PRIMITIVE_COLORS.neutral.white,
            iconOutline: PRIMITIVE_COLORS.semantic.yellow[700],
        },
        success: {
            background: PRIMITIVE_COLORS.semantic.green[500],
            backgroundOutline: PRIMITIVE_COLORS.semantic.green[50],
            border: PRIMITIVE_COLORS.semantic.green[500],
            text: PRIMITIVE_COLORS.neutral.white,
            textOutline: PRIMITIVE_COLORS.semantic.green[600],
            icon: PRIMITIVE_COLORS.neutral.white,
            iconOutline: PRIMITIVE_COLORS.semantic.green[600],
        },
        info: {
            background: PRIMITIVE_COLORS.semantic.blue[600],
            backgroundOutline: PRIMITIVE_COLORS.semantic.blue[50],
            border: PRIMITIVE_COLORS.semantic.blue[600],
            text: PRIMITIVE_COLORS.neutral.white,
            textOutline: PRIMITIVE_COLORS.semantic.blue[700],
            icon: PRIMITIVE_COLORS.neutral.white,
            iconOutline: PRIMITIVE_COLORS.semantic.blue[700],
        },
    },
});
